import React from 'react';
import GraphqlTable from 'cccisd-graphql-table';
import { assessmentPlans, instructors, responseFlags, learners, sites, supervisors } from '../renders';
import PageHeader from '../../../components/PageHeader';
import groupingUnits from './groupingUnits.graphql';

const now = new Date();

const AdminDashboard = () => {
    const renders = {
        sites: t => {
            if (t.isCsv) {
                return t.row['descendantGroups.siteCount'];
            }

            const siteList = t.row['descendantGroups.siteList'];
            const districtLabel = t.row['group.label'];
            return sites({
                siteList,
                context: districtLabel,
            });
        },
        learners: t => {
            if (t.isCsv) {
                return t.row['descendantRoles.learnerCount'];
            }

            const learnerList = t.row['descendantRoles.learnerList'];
            const districtLabel = t.row['group.label'];
            return learners({
                learnerList,
                context: districtLabel,
                adjustColumns: c => c.filter(col => col.label !== 'Student Name'),
            });
        },
        learnersActivePlans: t => {
            const learnerList = t.row['descendantRoles.learnerList'];
            const districtLabel = t.row['group.label'];

            const learnersInActivePlans = learnerList.filter(learner => {
                return learner.selectedAssignmentPlanList.some(plan => plan?.openSessions?.length > 0);
            });

            if (t.isCsv) {
                return learnersInActivePlans.length;
            }

            return learners({
                learnerList: learnersInActivePlans,
                context: districtLabel,
                adjustColumns: c => c.filter(col => col.label !== 'Student Name'),
            });
        },
        instructors: t => {
            if (t.isCsv) {
                return t.row['descendantRoles.instructorCount'];
            }

            const instructorList = t.row['descendantRoles.instructorList'];
            const districtLabel = t.row['group.label'];
            return instructors({ instructorList, context: districtLabel });
        },
        supervisors: t => {
            if (t.isCsv) {
                return t.row['descendantRoles.supervisorCount'];
            }

            const supervisorList = t.row['descendantRoles.supervisorList'];
            const districtLabel = t.row['group.label'];
            return supervisors({ supervisorList, context: districtLabel });
        },
        totalAssessmentPlans: t => {
            const instructorList = t.row['descendantRoles.instructorList'];
            const districtLabel = t.row['group.label'];
            const plans = [];
            instructorList.forEach(instructor => {
                if (instructor.createdAssignmentPlanCount > 0) {
                    instructor.createdAssignmentPlanList.forEach(plan => {
                        plans.push(plan);
                    });
                }
            });

            if (t.isCsv) {
                return plans.length;
            }

            return assessmentPlans({
                planList: plans,
                context: districtLabel,
                adjustColumns: c => {
                    return c.filter(col => col.label !== 'Actions');
                },
            });
        },
        activeAssessmentPlans: t => {
            const instructorList = t.row['descendantRoles.instructorList'];
            const districtLabel = t.row['group.label'];
            const plans = [];
            instructorList.forEach(instructor => {
                if (instructor.createdAssignmentPlanCount > 0) {
                    instructor.createdAssignmentPlanList.forEach(plan => {
                        if (plan.openSessions.length > 0) {
                            plans.push(plan);
                        }
                    });
                }
            });

            if (t.isCsv) {
                return plans.length;
            }

            return assessmentPlans({
                planList: plans,
                context: districtLabel,
                adjustColumns: c => {
                    return c.filter(col => col.label !== 'Actions');
                },
            });
        },
        responseFlags: t => {
            if (t.isCsv) {
                return t.row.flagCount;
            }

            const learnerList = t.row['descendantRoles.learnerList'];
            const districtLabel = t.row['group.label'];
            return responseFlags({ row: t.row, learnerList, context: districtLabel });
        },
    };

    return (
        <>
            <div className="container">
                <PageHeader
                    title="Admin Dashboard"
                    description="View assessment plan activity from every provider in your group"
                />
                <div style={{ background: '#fff', textAlign: 'center' }}>
                    <GraphqlTable
                        query={groupingUnits}
                        graphqlVariables={{ today: now.toJSON() }}
                        rowKey="group.groupId"
                        renders={renders}
                        columns={[
                            { name: 'group.label', label: 'District', filter: true, sort: true },
                            { name: 'descendantGroups.siteCount', render: 'sites', label: 'Schools', sort: true },
                            {
                                name: 'descendantRoles.learnerCount',
                                label: 'Total Students',
                                sort: true,
                                render: 'learners',
                            },
                            {
                                name: 'learners',
                                label: 'Students in Active Plans',
                                tooltip: 'Number of students with at least one active plan',
                                render: 'learnersActivePlans',
                            },
                            {
                                name: 'totalAssessmentPlanCount',
                                label: 'Total Assessment Plans',
                                tooltip: 'Number of Assessment Plans that have ever been created',
                                render: 'totalAssessmentPlans',
                            },
                            {
                                name: 'activeAssessmentPlanCount',
                                label: 'Active Assessment Plans',
                                tooltip: 'Number of Assessment Plans that are currently open',
                                render: 'activeAssessmentPlans',
                            },
                            {
                                name: 'descendantRoles.instructorCount',
                                label: 'Providers',
                                render: 'instructors',
                                sort: true,
                            },
                            {
                                name: 'descendantRoles.supervisorCount',
                                label: 'Supervisors',
                                render: 'supervisors',
                                sort: true,
                            },
                            {
                                name: 'flagCount',
                                label: 'Response Flags',
                                sort: true,
                                render: 'responseFlags',
                            },
                        ]}
                        csvFilename="districts"
                    />
                </div>
            </div>
        </>
    );
};

export default AdminDashboard;
